import { Head, router, usePage } from "@inertiajs/react"
import { Check, Copy, Pencil } from "lucide-react"
import { useMemo, useState } from "react"

import { HpiFilterSelect } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import {
  companyResourcesPath,
  companySignaturePath,
  companySignaturesPath,
  editCompanySignaturePath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface SignatureData {
  id: number
  name: string
  body: string | null
}

interface EmployeeData {
  id: number
  name: string
  formal_name: string | null
  job_title: string | null
  cell_phone: string | null
  email: string | null
  sig_show_cell: boolean
  sig_profile: string | null
  sig_vcard: string | null
}

interface EmployeeOption {
  id: number
  name: string
}

interface SignatureOption {
  id: number
  name: string
}

interface Props {
  signature: SignatureData
  employee: EmployeeData
  employees: EmployeeOption[]
  selected_employee_id: number
  signatures: SignatureOption[]
}

/* ── Helpers ────────────────────────────────────────────── */

function formatPhone(phone: string | null): string {
  if (!phone) return ""
  const digits = phone.replace(/\D/g, "")
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return phone
}

function renderSignature(body: string | null, employee: EmployeeData): string {
  if (!body) return ""
  let html = body
  html = html.replace(/\{\{empName\}\}/g, employee.formal_name ?? employee.name)
  html = html.replace(/\{\{empTitle\}\}/g, employee.job_title ?? "")

  // Only show cell phone if sig_show_cell is enabled (legacy: sig_showcell == 1)
  if (employee.sig_show_cell && employee.cell_phone) {
    html = html.replace(
      /\{\{empCell\}\}/g,
      ` <span style="color:#FDBA2F;">|</span> Cell: <a href="tel:${employee.cell_phone}" style="color:#547099;text-decoration:none;font-weight:normal;">${formatPhone(employee.cell_phone)}</a>`,
    )
  } else {
    html = html.replace(/\{\{empCell\}\}/g, "")
  }

  // Only show email if it contains hawkpi.com domain (legacy behavior)
  if (employee.email?.toLowerCase().includes("hawkpi.com")) {
    html = html.replace(/\{\{empEmail\}\}/g, employee.email)
  } else {
    html = html.replace(/\{\{empEmail\}\}/g, "")
  }

  // Profile bio link
  if (employee.sig_profile) {
    html = html.replace(
      /\{\{empProfile\}\}/g,
      ` <span style="color:#FDBA2F;">|</span> <a href="${employee.sig_profile}" style="color:#547099;text-decoration:none;font-weight:normal;">View Professional Bio</a>`,
    )
  } else {
    html = html.replace(/\{\{empProfile\}\}/g, "")
  }

  // vCard link
  if (employee.sig_vcard) {
    html = html.replace(
      /\{\{empVCard\}\}/g,
      `<a href="${employee.sig_vcard}" style="color:#547099;text-decoration:none;font-weight:normal;">Add to Contacts</a>`,
    )
  } else {
    html = html.replace(/\{\{empVCard\}\}/g, "")
  }

  return html
}

/* ── Main Component ────────────────────────────────────── */

export default function SignatureShow({
  signature,
  employee,
  employees,
  selected_employee_id,
  signatures,
}: Props) {
  const { auth } = usePage().props
  const [copied, setCopied] = useState(false)

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Company", href: companyResourcesPath() },
    { title: "Signatures", href: companySignaturesPath() },
    { title: signature.name, href: companySignaturePath(signature.id) },
  ]

  const renderedHtml = useMemo(
    () => renderSignature(signature.body, employee),
    [signature.body, employee],
  )

  const handleSignatureChange = (signatureId: string) => {
    router.get(
      companySignaturePath(Number(signatureId)),
      employees.length > 0 ? { employee_id: selected_employee_id } : {},
      { preserveState: true },
    )
  }

  const handleEmployeeChange = (employeeId: string) => {
    router.get(
      companySignaturePath(signature.id),
      { employee_id: employeeId },
      { preserveState: true },
    )
  }

  const handleCopy = async () => {
    try {
      const blob = new Blob([renderedHtml], { type: "text/html" })
      await navigator.clipboard.write([
        new ClipboardItem({ "text/html": blob }),
      ])
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback: copy as plain text
      const tmp = document.createElement("div")
      tmp.innerHTML = renderedHtml
      await navigator.clipboard.writeText(tmp.textContent ?? "")
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Signature - ${signature.name}`} />

      <div className="p-6">
        <div className="mb-6 flex flex-wrap items-center gap-4">
          {employees.length > 0 && (
            <HpiFilterSelect
              label="Employee"
              value={String(selected_employee_id)}
              onChange={handleEmployeeChange}
              options={employees.map((emp) => ({
                value: emp.id,
                label: emp.name,
              }))}
            />
          )}

          {signatures.length > 1 && (
            <HpiFilterSelect
              label="Location"
              value={String(signature.id)}
              onChange={handleSignatureChange}
              options={signatures.map((sig) => ({
                value: sig.id,
                label: sig.name,
              }))}
            />
          )}

          <button
            onClick={() => {
              void handleCopy()
            }}
            className="bg-hpi-active-blue hover:bg-hpi-dark-blue flex items-center gap-1.5 rounded px-4 py-2 text-sm font-medium text-white"
          >
            {copied ? (
              <Check className="h-4 w-4" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
            {copied ? "Copied!" : "Copy Signature"}
          </button>

          {auth.user.is_admin && (
            <button
              onClick={() =>
                router.visit(editCompanySignaturePath(signature.id))
              }
              className="border-hpi-light-gray hover:bg-hpi-pale-blue flex items-center gap-1.5 rounded border px-4 py-2 text-sm font-medium"
            >
              <Pencil className="h-4 w-4" />
              Edit
            </button>
          )}
        </div>

        <div className="border-hpi-light-gray rounded-lg border bg-white p-6">
          <div className="text-hpi-med-blue mb-3 text-xs font-medium tracking-wider uppercase">
            Preview — {signature.name}
          </div>
          <div
            className="signature-preview"
            dangerouslySetInnerHTML={{ __html: renderedHtml }}
          />
        </div>

        <div className="border-hpi-light-gray bg-hpi-pale-blue mt-6 rounded-lg border p-4">
          <h4 className="text-hpi-dark-gray mb-2 text-sm font-semibold">
            How to use
          </h4>
          <ol className="text-hpi-dark-gray space-y-1 text-sm">
            <li>1. Select the correct location template above</li>
            <li>2. Click &quot;Copy Signature&quot;</li>
            <li>3. In Outlook, go to Settings → Signatures</li>
            <li>4. Paste the copied signature</li>
          </ol>
        </div>
      </div>
    </AppLayout>
  )
}
